import React from "react";
import {
  Stack,
  Button,
  Link,
  Typography,
  OutlinedInput,
  Divider,
  FormControl,
  InputLabel,
  IconButton,
} from "@mui/material";
import LogLayout from "../../../Layouts/Log";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useToggle } from "../../../Utils/Hooks/useToggle";
import { useForm } from "react-hook-form";
import ModalLoginException, {
  ModalLoginExceptionProps,
} from "../../../Components/Modals/ModalLoginException/modal-login-exception";
import { ModalContext } from "../../../Components/Modals";
import { userService } from "../../../Core/Api/services";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { routePaths } from "../../../Router/router";
import { useLocation, useNavigate, useParams } from "react-router";
import FormInputPassword from "../../../Components/Forms/FormInputPassword/form-input-password";
import { useAppSelector } from "../../../Redux/redux-store";
import { GetFormIsValid } from "../../../Redux/Reducers/redux-slice-form-validation";
import { CheckPassword } from "../../../Utils/helpers";
import { useTranslation } from "react-i18next";

/*
Utility: Change password when is too old.
*/

const LoginNewPassword = () => {
  const { t } = useTranslation();
  interface FormData {
    login: string;
    oldPassword: string;
    newPassword: string;
    repeatNewPassword: string;
  }

  const params = useParams<{ login: string; password: string }>();
  const location = useLocation();
  const { active: visibleOldPassword, toggle: toggleOldPasswordVisibility } = useToggle(false);
  const { active: visiblePassword, toggle: togglePasswordVisibility } = useToggle(false);
  const { active: visibleRepeatPassword, toggle: toggleRepeatPasswordVisibility } = useToggle(false);
  const { register, watch, handleSubmit } = useForm<FormData>({
    defaultValues: {
      login: params.login ?? location.state.login ?? "",
      oldPassword: params.password ?? location.state.password ?? "",
      newPassword: "",
      repeatNewPassword: "",
    },
  });

  const formId = `new-password`;
  const formValid = useAppSelector(GetFormIsValid(formId));

  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };
  const enqueueCustomSnackbar = useCustomSnackbar();
  const navigate = useNavigate();

  function displayModal(title: string, message: string, advice: string): void {
    if (!openModal) return;

    const onClose: ModalLoginExceptionProps["onClose"] = (data) => {};

    openModal<ModalLoginExceptionProps>(ModalLoginException, {
      title: title,
      message: message,
      advice: advice,
      onClose,
    });
  }

  const onSubmit = handleSubmit(({ login, oldPassword, newPassword, repeatNewPassword }) => {
    let check = CheckPassword(newPassword, repeatNewPassword);

    if (!check.success) {
      displayModal(
        t("alert:warning.oopsMessage"),
        t("alert:error.newPasswordMustFollowInstructions", { message: check.errorMessages.join("\r\n- ") }),
        ""
      );
      return false;
    }

    userService
      .ChangePassword({
        login,
        oldPassword: oldPassword,
        newPassword: newPassword,
      })
      .then(function (response) {
        if (response.success) {
          navigate(routePaths.login.path);
          enqueueCustomSnackbar(t("alert:success.passHasBeenChanged"), "success", true);
        } else enqueueCustomSnackbar(t("alert:error.oopsWithMessage", { message: response.message }), "warning");
      });
  });

  return (
    <LogLayout sxRightContainer={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
      <Stack alignItems="center">
        <Typography variant="h3" textAlign="center">
          {t("page:body.pleaseCreateNewPassword")}
        </Typography>
      </Stack>
      <Divider sx={{ mt: 2, mb: 4 }} />
      <form onSubmit={onSubmit}>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel shrink htmlFor="email" sx={{ flex: 1 }}>
            {t("common:login")}*<Typography component="span" sx={{ color: "rgba(255, 255, 255, 0.7)" }}></Typography>
          </InputLabel>
          <OutlinedInput id="email" {...register("login")} />
        </FormControl>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel shrink htmlFor="password" sx={{ flex: 1 }}>
            {t("field:label.oldPassword")}*
          </InputLabel>
          <OutlinedInput
            type={visibleOldPassword ? "text" : "password"}
            {...register("oldPassword")}
            autoComplete="none"
            required
            id="oldPassword"
            endAdornment={
              <IconButton sx={{ mr: -1 }} onClick={toggleOldPasswordVisibility}>
                {visibleOldPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </IconButton>
            }
          />
        </FormControl>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <FormInputPassword
            register={register}
            watch={watch}
            label={t("common:newPassword")}
            validationProps={{
              formId: formId,
              fieldId: "dueDate",
              required: true,
            }}
          />
        </FormControl>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel shrink htmlFor="password-repeat" sx={{ flex: 1 }}>
            {t("field:label.repeatNewPassword")}*
          </InputLabel>
          <OutlinedInput
            type={visibleRepeatPassword ? "text" : "password"}
            {...register("repeatNewPassword")}
            autoComplete="none"
            required
            id="password-repeat"
            endAdornment={
              <IconButton sx={{ mr: -1 }} onClick={toggleRepeatPasswordVisibility}>
                {visibleRepeatPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </IconButton>
            }
          />
        </FormControl>
        <Stack alignItems="center" mt={2}>
          <Button
            variant="contained"
            fullWidth
            size="large"
            endIcon={<ArrowForwardIcon />}
            type="submit"
            disabled={!formValid}
          >
            {t("button:confirmMyPassword")}
          </Button>
        </Stack>
        <Stack direction="row" justifyContent="space-between" mt={3.5} spacing={3}>
          <Typography>
            {t("page:body.doYouNeedHelp")}{" "}
            <Link underline="hover" color="primary.text">
              {t("button:contactUs")}
            </Link>
          </Typography>
          {/*<LanguageSwitch lang="fr" />*/}
        </Stack>
      </form>
    </LogLayout>
  );
};

export default LoginNewPassword;
